import styled from 'styled-components';
import React, {FC} from 'react';

import {StylableProps} from '../../lib/styleHelpers';
import {
  fontFamiliesStrings,
  fontSizesMixins,
  fontWeights,
  palette,
  mediaBreakpointUp,
  mediaBreakpointDown
} from '../../lib/styleVariables';
import {paletteR4Secondary} from '../../lib/styleVariablesR4';
import {TertiaryLink} from '../molecules/buttonsR4';
import {Icon, IconName} from './icon';
import {ImageProps, Image} from '../common/image';

interface ImageTextPublicProps extends StylableProps {
  size?: 'xs' | 's';
  type?: 'centered' | 'grid';
  direction?: 'row' | 'column';
  iconBackgroundColor?: string;
  designVersion?: 'v1' | 'r4';
}

export interface ImageTextBlockProps extends ImageTextPublicProps {
  backgroundColor?: string;
  description?: string;
  iconName?: IconName;
  iconColor?: 'string';
  imageType?: 'contain' | 'original';
  imgSrc?: ImageProps['fluid'];
  title?: string;
  subtitle?: string;
  linkText?: string;
  linkHref?: string;
}

const StyledContainer = styled.div<ImageTextPublicProps>`
  align-items: ${props =>
    (props.type === 'grid' && props.designVersion === 'r4' && props.direction === 'row'
      ? 'flex-start'
      : 'center')};
  display: flex;
  flex-direction: column;
  max-width: ${props => (props.size === 'xs' ? '304px' : '')};
  text-align: ${props =>
    (props.size === 'xs' &&
    !(props.type === 'grid' && props.designVersion === 'r4' && props.direction === 'row')
      ? 'center'
      : 'left')};

  ${mediaBreakpointDown('mobile')} {
    text-align: ${props => (props.size === 'xs' ? 'center' : 'left')};
    align-items: center;
  }
`;

const getIconMarginBottom = (props: ImageTextPublicProps) => {
  if (props.type === 'grid' && props.designVersion === 'r4')
    return '20px';
  if (props.type === 'centered')
    return '30px';

  return '0';
};

const StyledIconWrapper = styled.div<ImageTextPublicProps>`
  background-color: ${props =>
    (props.type === 'centered' || (props.type === 'grid' && props.designVersion === 'r4')
      ? props.iconBackgroundColor || palette.blue.highlight
      : '')};
  margin-bottom: ${props => getIconMarginBottom(props)};
  border-radius: ${props => (props.type === 'grid' && props.designVersion === 'r4' ? '8px' : '50%')};
  padding: ${props => (props.type === 'grid' && props.designVersion === 'r4' ? '8px' : '16px')};
`;

const StyledContainImage = styled(Image)`
  width: 100%;
  height: 240px;
  margin-bottom: 32px;
  object-fit: contain;
  border-radius: 8px;
  display: block;
  ${mediaBreakpointDown('tablet')} {
    height: auto;
    margin-bottom: 25px;
  }
  > img,
  > picture img {
    object-fit: contain !important;
  }
`;

const StyledImageContainer = styled.div<{backgroundColor?: string}>`
  width: 100%;
  height: 240px;
  margin-bottom: 32px;
  border-radius: 8px;
  background: ${props => props.backgroundColor || palette.white.base};
  display: flex;
  align-items: center;
  justify-content: center;

  ${mediaBreakpointDown('tablet')} {
    height: 176px;
    margin-bottom: 25px;
    background: ${palette.white.highlight};
  }
`;

const StyledImage = styled(Image)`
  width: 55%;
  height: 55%;

  > img,
  > picture img {
    object-fit: contain !important;
  }

  ${mediaBreakpointDown('tablet')} {
    width: 72.5%;
    height: 72.5%;
  }
`;

const StyledDescription = styled.span<ImageTextPublicProps>`
  ${fontSizesMixins.text2()};
  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixins.text3()};
    line-height: 26px;
  }
`;

const StyledTitle = styled.span<ImageTextPublicProps>`
  ${fontSizesMixins.h3()}
  font-weight: ${props =>
    (props.type === 'grid' && props.designVersion === 'r4' ? fontWeights.semibold : fontWeights.medium)};
  font-family: ${fontFamiliesStrings.title};
  margin-bottom: ${props => (props.type === 'grid' && props.designVersion === 'r4' ? '24px' : '8px')};

  ${mediaBreakpointDown('tablet')} {
    line-height: 24px;
  }

  ${mediaBreakpointDown('mobile')} {
    font-weight: ${fontWeights.normal};
    line-height: 30px;
  }
`;

const StyledContent = styled.div<ImageTextPublicProps>`
  display: flex;
  flex-direction: column;
  padding: 0 ${props => (props.size === 'xs' ? '0' : '16px')};

  ${mediaBreakpointDown('tablet')} {
    align-items: ${props => (props.size === 's' ? 'center' : '')};
    text-align: ${props => (props.size === 's' ? 'center' : '')};
  }
`;
const linkXSmallStyle = `
  align-self: center;
`;

const StyledTertiaryLink = styled(TertiaryLink)<{isXSmall: boolean}>`
  margin-top: 16px;
  color: ${paletteR4Secondary.blue.lowlight};
  ${mediaBreakpointUp('desktop')} {
    align-self: flex-start; /* Force button to behave like inline-block */
  }

  ${mediaBreakpointDown('tablet')} {
    margin-top: 8px;
  }
  ${props => props.isXSmall && linkXSmallStyle}
`;

const getIconColor = (props: ImageTextBlockProps) => {
  if (props.type === 'centered')
    return palette.blue.base;
  if (props.type === 'grid' && props.designVersion === 'r4')
    return props.iconColor;

  return palette.green.base;
};

export const ImageTextBlock: FC<ImageTextBlockProps> = props => (
  <StyledContainer
    className={props.className}
    size={props.size}
    type={props.type}
    designVersion={props.designVersion}
    direction={props.direction}
  >
    {props.size === 'xs' && props.iconName && (
      <StyledIconWrapper
        type={props.type}
        iconBackgroundColor={props.iconBackgroundColor}
        designVersion={props.designVersion}
      >
        <Icon size={24} color={getIconColor(props)} name={props.iconName || 'checkmarkCircle'} />
      </StyledIconWrapper>
    )}
    {props.size === 's' &&
      props.imgSrc &&
      (props.imageType === 'original' ? (
        <StyledImageContainer backgroundColor={props.backgroundColor}>
          <StyledImage fluid={props.imgSrc} />
        </StyledImageContainer>
      ) : (
        <StyledContainImage fluid={props.imgSrc} />
      ))}
    <StyledContent size={props.size}>
      {props.title && (
        <StyledTitle type={props.type} designVersion={props.designVersion}>
          {props.title}
        </StyledTitle>
      )}
      {props.subtitle && <span>{props.subtitle}</span>}
      {props.description && <StyledDescription size={props.size}>{props.description}</StyledDescription>}
      {props.linkHref && (
        <StyledTertiaryLink isXSmall={props.size === 'xs'} href={props.linkHref}>
          {props.linkText}
        </StyledTertiaryLink>
      )}
    </StyledContent>
  </StyledContainer>
);
